/**
 * Auftrag abnehmen – der Abschluss.
 *
 * Erfasst wird mehr als das Datum: Was am Ende weniger hereinkam als
 * beauftragt, und warum. Diese Differenz ist die interessante Größe für jede
 * spätere Auswertung – ohne sie weiß man nur, was beauftragt wurde.
 *
 * Der Nachlass ist kein Pflichtfeld: Der Regelfall ist, dass nichts
 * nachgelassen wurde. Wird aber einer eingetragen, ist die Begründung Pflicht.
 * Eine Zahl ohne Grund ist in einem Jahr wertlos.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api';
import { useKora } from '../zustand';
import { Modal, KnopfPrimaer, KnopfSekundaer } from '../komponenten/Modal';
import { betragFormatieren, betragLesen, eur } from '../hilfen/format';
import type { Angebot, AngebotAntwort } from '../typen';

export function AbnahmeModal({
  angebot,
  onSchliessen,
  onFertig,
}: {
  angebot: Angebot;
  onSchliessen: () => void;
  onFertig: () => void;
}) {
  const { toastZeigen, meta } = useKora();
  const [datum, setDatum] = useState(meta?.heute ?? '');
  const [nachlass, setNachlass] = useState('');
  const [grund, setGrund] = useState('');
  const [fehler, setFehler] = useState('');

  const betrag = nachlass.trim() ? (betragLesen(nachlass) ?? 0) : 0;
  const rest = (angebot.auftrag ?? 0) - betrag;

  const speichern = useMutation({
    mutationFn: () =>
      api.sende<AngebotAntwort>(`/angebote/${angebot.id}/abnahme`, {
        datum,
        nachlass: betrag,
        grund: grund.trim(),
      }),
    onSuccess: (a) => {
      toastZeigen(a.toast ?? 'Auftrag abgenommen.');
      onFertig();
      onSchliessen();
    },
    onError: (f) => setFehler(f instanceof Error ? f.message : 'Abnahme fehlgeschlagen.'),
  });

  return (
    <Modal
      titel="Auftrag abnehmen"
      untertitel={`${angebot.nr}_${angebot.prefix}`}
      breite={520}
      abstandOben={70}
      fest={speichern.isPending}
      onSchliessen={onSchliessen}
      fuss={
        <>
          <KnopfSekundaer onClick={onSchliessen} disabled={speichern.isPending}>
            Abbrechen
          </KnopfSekundaer>
          <KnopfPrimaer
            ton="gruen"
            onClick={() => speichern.mutate()}
            disabled={speichern.isPending || !datum || (betrag > 0 && !grund.trim())}
          >
            {speichern.isPending ? 'Wird gespeichert …' : 'Auftrag abnehmen'}
          </KnopfPrimaer>
        </>
      }
    >
      <div className="modal-hinweis">
        Damit ist der Vorgang abgeschlossen. Er verschwindet aus der Auftragsübersicht und bleibt in
        der Datenbank erhalten.
      </div>

      <label className="modal-feld">
        <span>Abnahmedatum</span>
        <input
          type="date"
          className="modal-eingabe"
          value={datum}
          onChange={(e) => {
            setDatum(e.target.value);
            setFehler('');
          }}
        />
      </label>

      <label className="modal-feld">
        <span>Nachlass oder Verlust · optional</span>
        <input
          className="modal-eingabe"
          inputMode="numeric"
          placeholder="0"
          value={nachlass}
          onChange={(e) => {
            setNachlass(e.target.value);
            setFehler('');
          }}
          onBlur={(e) => setNachlass(betragFormatieren(e.target.value))}
        />
        <span className="modal-hinweis">
          Was am Ende weniger hereinkam als beauftragt – ein gewährter Nachlass, eine Gutschrift,
          ein Fehler, der uns etwas gekostet hat.
        </span>
      </label>

      {betrag > 0 && (
        <>
          <label className="modal-feld">
            <span>Warum</span>
            <input
              className="modal-eingabe"
              value={grund}
              placeholder="Kurz, aber nachvollziehbar"
              onChange={(e) => {
                setGrund(e.target.value);
                setFehler('');
              }}
            />
          </label>

          <div className="modal-hinweis">
            Beauftragt {eur(angebot.auftrag)} · Nachlass {eur(betrag)} · geblieben{' '}
            <strong>{eur(rest)}</strong>
          </div>
        </>
      )}

      {fehler && <div className="modal-fehler">{fehler}</div>}


    </Modal>
  );
}